import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { sessionStore } from '@/core/auth/session.store.js';
import { toAppError } from '@/core/errors/app-error.js';

import {
  hasErrors,
  normalizeCredentials,
  validateCredentials,
  type Credentials,
  type CredentialsErrors,
} from '../domain/credentials.js';
import { login } from '../infrastructure/auth.api.js';

/**
 * Caso de uso de inicio de sesión.
 *
 * Valida en el dominio antes de salir a la red: un formulario incompleto no
 * gasta un viaje ni expone el usuario a un mensaje genérico del servidor.
 */
export function useLogin() {
  const [fieldErrors, setFieldErrors] = useState<CredentialsErrors>({});
  const setSession = sessionStore((state) => state.setSession);

  const mutation = useMutation({
    mutationFn: (credentials: Credentials) => login(normalizeCredentials(credentials)),
    onSuccess: (session) => {
      setSession(session);
    },
  });

  const submit = useCallback(
    (credentials: Credentials) => {
      const errors = validateCredentials(credentials);
      setFieldErrors(errors);
      if (hasErrors(errors)) {
        return;
      }
      mutation.mutate(credentials);
    },
    [mutation],
  );

  return {
    submit,
    fieldErrors,
    isSubmitting: mutation.isPending,
    error: mutation.error === null ? undefined : toAppError(mutation.error),
  };
}
